import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Building2, Heart, Building, Zap, Code, CheckCircle2 } from 'lucide-react';
import { industries } from '../data/mock';

const Industries = () => {
  const iconMap = {
    Building2: Building2,
    Heart: Heart,
    Building: Building,
    Zap: Zap,
    Code: Code
  };

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section className="industries-hero">
        <div className="industries-hero-content">
          <Badge className="hero-badge">Industry Solutions</Badge>
          <h1 className="industries-hero-title">
            Specialized Security for Your Industry
          </h1>
          <p className="industries-hero-description">
            Tailored cybersecurity solutions addressing unique threats, compliance requirements, and operational challenges across industries.
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="section">
        <div className="section-content">
          {industries.map((industry, index) => {
            const IconComponent = iconMap[industry.icon];
            const isEven = index % 2 === 0;
            
            return (
              <div 
                key={industry.id} 
                id={industry.name.toLowerCase()}
                className={`industry-detail ${isEven ? 'bg-white' : 'bg-light'}`}
              >
                <div className={`industry-detail-grid ${!isEven ? 'reverse' : ''}`}>
                  <div className="industry-detail-image">
                    <img 
                      src={industry.image} 
                      alt={industry.fullName}
                      className="industry-img"
                    />
                  </div>
                  <div className="industry-detail-content">
                    <div className="industry-detail-icon">
                      <IconComponent className="icon" />
                    </div>
                    <h2 className="industry-detail-title">{industry.fullName}</h2>
                    <p className="industry-detail-description">{industry.description}</p>

                    {/* Threat Landscape */}
                    <div className="industry-section">
                      <h3 className="industry-section-title">Threat Landscape</h3>
                      <ul className="industry-list">
                        {industry.threatLandscape.map((threat, idx) => (
                          <li key={idx} className="industry-item">
                            <span className="threat-bullet">•</span>
                            <span>{threat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Regulatory Requirements */}
                    <div className="industry-section">
                      <h3 className="industry-section-title">Regulatory Requirements</h3>
                      <div className="regulations-badges">
                        {industry.regulations.map((reg, idx) => (
                          <Badge key={idx} variant="secondary" className="regulation-badge-large">
                            {reg}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* How We Solve */}
                    <div className="industry-section">
                      <h3 className="industry-section-title">How IrisTechLabs Solves It</h3>
                      <ul className="industry-list">
                        {industry.solutions.map((solution, idx) => (
                          <li key={idx} className="industry-item">
                            <CheckCircle2 className="solution-check" />
                            <span>{solution}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Button asChild size="lg" className="industry-cta">
                      <Link to="/contact">Get Industry Assessment</Link>
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* BFSI Focus Section */}
      <section className="section bfsi-focus">
        <div className="section-content">
          <div className="section-header">
            <h2 className="section-title">BFSI: Our Primary Focus for India</h2>
            <p className="section-description">
              Deep expertise in Indian banking and financial services cybersecurity
            </p>
          </div>
          <div className="bfsi-grid">
            <Card className="bfsi-card">
              <CardHeader>
                <CardTitle>RBI Alignment</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Complete alignment with RBI Master Direction on Cyber Resilience and Digital Payment Security Controls</p>
              </CardContent>
            </Card>
            <Card className="bfsi-card">
              <CardHeader>
                <CardTitle>DPDP India Expertise</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Specialized services for Digital Personal Data Protection Act compliance in financial services</p>
              </CardContent>
            </Card>
            <Card className="bfsi-card">
              <CardHeader>
                <CardTitle>Financial Fraud Detection</CardTitle>
              </CardHeader>
              <CardContent>
                <p>AI-powered detection of account takeover, payment fraud, and insider threats specific to BFSI</p>
              </CardContent>
            </Card>
            <Card className="bfsi-card">
              <CardHeader>
                <CardTitle>Zero Trust for Banking</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Zero Trust Architecture implementation for digital banking and core banking systems</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">Get Your Industry-Specific Security Assessment</h2>
          <p className="cta-description">
            Talk to our industry experts about your unique security challenges
          </p>
          <Button asChild size="lg" className="cta-button-primary">
            <Link to="/contact">Schedule Consultation</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Industries;
